import React, { useState, useEffect } from 'react';
import { Layers, ArrowRight, Loader2 } from 'lucide-react';
import api from '../services/api';

/**
 * MainGroupsTooltip - Rich tooltip showing main groups breakdown
 * with parts count, subgroups count, and visual density bars
 */
const MainGroupsTooltip = ({ taxonomyStats, onGroupClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Get main groups with their stats
      const response = await api.get('/api/v1/taxonomy/main-groups');
      const mainGroups = response.data || [];
      
      // Sort by part count descending
      const sorted = [...mainGroups].sort((a, b) => (b.part_count || 0) - (a.part_count || 0));
      
      // Calculate max for density bars
      const maxParts = Math.max(...sorted.map(g => g.part_count || 0), 1);
      
      // Calculate insight
      const top3Parts = sorted.slice(0, 3).reduce((sum, g) => sum + (g.part_count || 0), 0);
      const totalParts = sorted.reduce((sum, g) => sum + (g.part_count || 0), 0);
      const top3Percentage = totalParts > 0 ? Math.round((top3Parts / totalParts) * 100) : 0;
      
      setData({
        groups: sorted,
        maxParts,
        insight: `Top 3 groups contain ${top3Percentage}% of all parts`,
        total: mainGroups.length
      });
    } catch (error) {
      console.error('Failed to load main groups data:', error);
      setData({ groups: [], maxParts: 1, insight: '', total: 0 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-white" />
            <span className="font-semibold text-white text-sm">Main Groups</span>
          </div>
        </div>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-white" />
            <span className="font-semibold text-white text-sm">Main Groups</span>
          </div>
          <span className="text-purple-200 text-xs">{data.total} total</span>
        </div>
      </div>

      {/* Table */}
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 font-medium text-slate-500">Group</th>
              <th className="text-right px-3 py-2 font-medium text-slate-500">Parts</th>
              <th className="text-right px-3 py-2 font-medium text-slate-500">Subs</th>
              <th className="px-3 py-2 font-medium text-slate-500 w-20">Density</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((group) => {
              const density = data.maxParts > 0 ? ((group.part_count || 0) / data.maxParts) * 100 : 0;
              return (
                <tr 
                  key={group.code}
                  onClick={() => onGroupClick?.(group.code)}
                  className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-2">
                    <span className="font-mono text-purple-600 font-semibold">{group.code}</span>
                    <span className="text-slate-600 ml-1.5 truncate">{group.name?.split(' ')[0]}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-700">
                    {(group.part_count || 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500">
                    {group.subgroup_count || 0}
                  </td>
                  <td className="px-3 py-2">
                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{ width: `${Math.max(density, 2)}%` }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Insight Footer */}
      {data.insight && (
        <div className="px-3 py-2 bg-amber-50 border-t border-amber-100">
          <p className="text-xs text-amber-800">
            <span className="font-semibold">💡</span> {data.insight}
          </p>
        </div>
      )}

      {/* View All Link */}
      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200">
        <a 
          href="/taxonomy" 
          className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1 justify-center"
        >
          View All in Taxonomy Manager
          <ArrowRight className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export default MainGroupsTooltip;
